import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { scrollToEl, scrollToTop } from "../Utility/scroll";
import logo from "../Assets/logo.png";

const EASE = [0.76, 0, 0.24, 1];

/**
 * روابط النافبار. اللي بيبدأ بـ "/#" بيودّي لقسم جوه الصفحة الرئيسية،
 * واللي من غير # بيفتح صفحة مستقلة. الأسماء بقت في ملفات اللغات (t.nav.links).
 */
export const LINKS = [
  { id: "home", to: "/#home" },
  { id: "about", to: "/about-us" },
  { id: "projects", to: "/projects" },
  { id: "developers", to: "/#developers" },
  { id: "blogs", to: "/blogs" },
  { id: "contact", to: "/contact-us" },
];
export const CTA_TO = "/contact-us";

// أقسام الصفحة الرئيسية بالترتيب، وأنهي رابط يتفعّل لما تكون واقف في القسم ده
const SPY = [
  ["home", "home"],
  ["about", "about"],
  ["developers", "developers"],
  ["projects", "projects"],
  ["blogs", "blogs"],
  ["testimonials", null],
  ["contact", "contact"],
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } };
const item = {
  hidden: { opacity: 0, y: -22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};
const brandItem = {
  hidden: { opacity: 0, y: -26, scale: 0.9 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 1.1, ease: EASE } },
};

// الـ Preloader بيحط data-intro="wait" على <html> وبعدين "go" لحظة ما يفتح.
const introReady = () => document.documentElement.getAttribute("data-intro") !== "wait";
const useIntroGo = () => {
  const [go, setGo] = useState(introReady);
  useEffect(() => {
    if (go) return;
    const mo = new MutationObserver(() => {
      if (introReady()) {
        setGo(true);
        mo.disconnect();
      }
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-intro"] });
    return () => mo.disconnect();
  }, [go]);
  return go;
};

const Globe = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
  </svg>
);

// قائمة اللغات: روابط حقيقية (جوجل بيقدر يتبعها) لنفس الصفحة بكل لغة
const LangMenu = () => {
  const { lang, language, languages, hrefFor, t } = useLang();
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="langsw" ref={wrap}>
      <button
        type="button"
        className="nav__lang langsw__btn"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t.nav.language}: ${language.label}`}
        onClick={() => setOpen((o) => !o)}
      >
        <Globe />
        <span>{language.short}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            className="langsw__menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            {languages.map((l) => (
              <li key={l.code}>
                <a
                  href={hrefFor(l.code, pathname)}
                  hrefLang={l.hreflang || l.code}
                  lang={l.code}
                  className={`langsw__opt ${l.code === lang ? "is-on" : ""}`}
                  aria-current={l.code === lang ? "true" : undefined}
                >
                  <span>{l.label}</span>
                  <small>{l.short}</small>
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbarefixed = () => {
  const { t, isRTL, lang, languages, hrefFor } = useLang();
  const { pathname, hash } = useLocation();
  const go = useIntroGo();

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [spy, setSpy] = useState("home");
  const { scrollY } = useScroll();
  const raf = useRef(0);

  // أنهي قسم واقف فيه (للصفحة الرئيسية بس)
  const computeSpy = useCallback(() => {
    if (pathname !== "/") return;
    let cur = "home";
    for (const [id, navId] of SPY) {
      const el = document.getElementById(id);
      if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.35) cur = navId;
    }
    setSpy(cur);
  }, [pathname]);

  // مرة واحدة كل فريم بحد أقصى (أخف على الموبايل)
  useMotionValueEvent(scrollY, "change", (y) => {
    setScrolled(y > 60);
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      computeSpy();
    });
  });
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  useEffect(() => {
    const timer = setTimeout(computeSpy, 150);
    return () => clearTimeout(timer);
  }, [computeSpy, hash]);

  useEffect(() => setOpen(false), [pathname, hash]);

  const routeActive = pathname.startsWith("/about-us")
    ? "about"
    : pathname.startsWith("/blogs")
    ? "blogs"
    : pathname.startsWith("/contact-us")
    ? "contact"
    : pathname.startsWith("/developers")
    ? "developers"
    : pathname.startsWith("/project")
    ? "projects"
    : null;
  const activeId = pathname === "/" ? spy : routeActive;

  const onNav = (e, to) => {
    setOpen(false);
    const [path, targetHash] = to.split("#");
    const samePath = pathname === (path || "/");
    const sameHash = targetHash ? hash === `#${targetHash}` : !hash;
    if (samePath && sameHash) {
      e.preventDefault();
      const el = targetHash ? document.getElementById(targetHash) : null;
      if (el) scrollToEl(el);
      else scrollToTop();
    }
  };

  const cls = ["nav", scrolled && "nav--solid"].filter(Boolean).join(" ");
  const corner = isRTL ? "0%" : "100%";

  return (
    <>
      <motion.header className={cls} variants={container} initial="hidden" animate={go ? "show" : "hidden"}>
        <motion.div variants={brandItem} style={{ justifySelf: "start" }}>
          <Link to="/#home" className="nav__brand" aria-label={t.brandFull} onClick={(e) => onNav(e, "/#home")}>
            <img className="nav__logo" src={logo} alt={t.brandFull} width="160" height="80" decoding="async" />
          </Link>
        </motion.div>

        <nav className="nav__links" aria-label="Main">
          {LINKS.map((k) => (
            <motion.span key={k.id} variants={item}>
              <Link
                to={k.to}
                className={`nav__link ${activeId === k.id ? "is-active" : ""}`}
                aria-current={activeId === k.id ? "page" : undefined}
                onClick={(e) => onNav(e, k.to)}
              >
                {t.nav.links[k.id]}
              </Link>
            </motion.span>
          ))}
        </nav>

        <div className="nav__actions">
          <motion.div variants={item} className="nav__lang-wrap">
            <LangMenu />
          </motion.div>
          <motion.div variants={item} className="nav__cta-wrap">
            <Link to={CTA_TO} className="btn btn--ghost nav__cta" onClick={(e) => onNav(e, CTA_TO)}>
              {t.nav.cta}
            </Link>
          </motion.div>
          <motion.button
            type="button"
            variants={item}
            className={`burger ${open ? "burger--open" : ""}`}
            aria-label={t.nav.menu}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <span />
            <span />
          </motion.button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="menu"
            initial={{ clipPath: `circle(0% at ${corner} 0%)` }}
            animate={{ clipPath: `circle(150% at ${corner} 0%)` }}
            exit={{ clipPath: `circle(0% at ${corner} 0%)` }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <nav className="menu__links">
              {[...LINKS, { id: "guide", to: "/off-plan-guide" }].map((k, i) => (
                <motion.div
                  key={k.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.07, duration: 0.7, ease: EASE }}
                >
                  <Link to={k.to} className={activeId === k.id ? "is-active" : ""} onClick={(e) => onNav(e, k.to)}>
                    {t.nav.links[k.id]}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              className="menu__langs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.6 }}
            >
              {languages.map((l) => (
                <a
                  key={l.code}
                  href={hrefFor(l.code, pathname)}
                  hrefLang={l.hreflang || l.code}
                  lang={l.code}
                  className={`cchip ${l.code === lang ? "is-on" : ""}`}
                >
                  {l.label}
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
